// recently-viewed-db.js (Backend Only)

class RecentlyViewedDB {
  constructor() {
    this.supabaseUrl = "https://penkozrfaxruujxpotdn.supabase.co";
    this.supabaseKey =
      "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBlbmtvenJmYXhydXVqeHBvdGRuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTM5NDIyNzYsImV4cCI6MjA2OTUxODI3Nn0.peEO1604jU2dxZD2ZQ1OYLUsxs8sNmK-7MIUQZ8E7Rw";
    this.maxItems = 8;

    this.container = document.getElementById("rv-db-section");
    this.grid = document.getElementById("rv-db-grid");
    this.customerId = this.container?.dataset.customerId;

    console.log("[RecentlyViewedDB] Constructor:", {
      supabaseUrl: this.supabaseUrl,
      customerId: this.customerId,
      container: this.container,
      grid: this.grid,
    });

    if (typeof supabase === "undefined") {
      console.error("Recently Viewed DB Error: Supabase client not loaded.");
      return;
    }

    this.sbClient = supabase.createClient(this.supabaseUrl, this.supabaseKey);
    console.log("[RecentlyViewedDB] Supabase client created:", this.sbClient);
  }

  init() {
    console.log("[RecentlyViewedDB] init called");
    // If a container exists on the page for a logged-in customer, render the products
    if (this.container && this.grid && this.customerId) {
      this.render();
    } else {
      console.warn(
        "[RecentlyViewedDB] Container, grid, or customerId missing:",
        this.container,
        this.grid,
        this.customerId
      );
    }
  }

  async track(productHandle) {
    console.log(
      "[RecentlyViewedDB] track called with:",
      productHandle,
      "customerId:",
      this.customerId
    );
    if (!productHandle || !this.customerId) {
      console.warn(
        "[RecentlyViewedDB] track: productHandle or customerId missing",
        productHandle,
        this.customerId
      );
      return;
    }

    try {
      // Upsert ensures the item is added and the timestamp is updated if it already exists.
      // This brings it to the "front" of the list when sorted by date.
      await this.sbClient.from("recently_viewed_items").upsert(
        {
          customer_id: this.customerId,
          product_handle: productHandle,
          created_at: new Date().toISOString(),
        },
        { onConflict: "customer_id, product_handle" }
      );
      console.log("[RecentlyViewedDB] upserted to Supabase:", productHandle);

      // FIFO Logic: After adding, check if we exceed the max count and delete the oldest item.
      const { data, error } = await this.sbClient
        .from("recently_viewed_items")
        .select("id, created_at")
        .eq("customer_id", this.customerId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      console.log("[RecentlyViewedDB] Fetched items after upsert:", data);

      if (data.length > this.maxItems) {
        const oldestItem = data[data.length - 1];
        await this.sbClient
          .from("recently_viewed_items")
          .delete()
          .eq("id", oldestItem.id);
        console.log("[RecentlyViewedDB] Deleted oldest item:", oldestItem);
      }
    } catch (error) {
      console.error("Error tracking recently viewed item:", error);
    }
  }

  async render() {
    console.log("[RecentlyViewedDB] render called");

    // Show skeleton loaders initially (they're already in HTML)
    this.showSkeletonLoaders();

    try {
      const { data, error } = await this.sbClient
        .from("recently_viewed_items")
        .select("product_handle")
        .eq("customer_id", this.customerId)
        .order("created_at", { ascending: false })
        .limit(this.maxItems);

      if (error) throw error;

      console.log("[RecentlyViewedDB] render fetched handles:", data);
      const handles = data.map((item) => item.product_handle);

      if (handles.length === 0) {
        this.clearSkeletonLoaders();
        this.container.style.display = "none";
        console.log("[RecentlyViewedDB] No handles to render, hiding section.");
        return;
      }

      await this.renderProductCards(handles, "logged-in");
      this.setupCarousel(handles.length);
    } catch (error) {
      console.error("Error rendering recently viewed:", error);
      this.clearSkeletonLoaders();
      this.container.style.display = "none";
    }
  }

  async renderGuest() {
    console.log("[RecentlyViewedDB] renderGuest called");

    // Show skeleton loaders initially (they're already in HTML)
    this.showSkeletonLoaders();

    let handles = [];
    try {
      handles = JSON.parse(
        localStorage.getItem("recently_viewed_handles") || "[]"
      );
    } catch (e) {
      handles = [];
    }

    if (!Array.isArray(handles) || handles.length === 0) {
      this.clearSkeletonLoaders();
      this.container.style.display = "none";
      console.log(
        "[RecentlyViewedDB] No guest handles to render, hiding section."
      );
      return;
    }

    await this.renderProductCards(handles, "guest");
    this.setupCarousel(handles.length);
  }

  async renderProductCards(handles, userType) {
    console.log(
      `[RecentlyViewedDB] Rendering product cards for ${userType} user`
    );

    // Clear skeleton loaders before adding real products
    this.clearSkeletonLoaders();

    for (const handle of handles.slice(0, this.maxItems)) {
      try {
        // Fetch pre-rendered product card HTML from alternate template
        const res = await fetch(`/products/${handle}?view=card`);
        if (res.ok) {
          const html = await res.text();
          const wrapper = document.createElement("div");
          wrapper.innerHTML = html;

          // Find the product-card root element
          const card = wrapper.querySelector(
            ".product-card, .card-wrapper, .card"
          );

          if (card) {
            this.grid.appendChild(card);
            console.log(
              `[RecentlyViewedDB] Rendered product card (${userType}):`,
              handle
            );
          } else {
            console.warn(
              "[RecentlyViewedDB] No product card found in HTML for",
              handle
            );
            // Log the HTML to debug what's being returned
            console.log("[RecentlyViewedDB] HTML received:", html);
          }
        } else {
          console.warn(
            "[RecentlyViewedDB] Failed to fetch card for",
            handle,
            res.status
          );
        }
      } catch (err) {
        console.error(
          "[RecentlyViewedDB] Error fetching card for",
          handle,
          err
        );
      }
    }
  }

  setupCarousel(itemCount) {
    console.log(
      "[RecentlyViewedDB] Setting up carousel with",
      itemCount,
      "items"
    );

    const prevButton = this.container.querySelector(
      ".rv-carousel-button--prev"
    );
    const nextButton = this.container.querySelector(
      ".rv-carousel-button--next"
    );
    const sliderButtons = this.container.querySelector(".rv-db-slider-buttons");
    const sliderCounter = this.container.querySelector(
      ".slider-counter--total"
    );

    // Always show carousel controls since it's always carousel
    if (prevButton && nextButton) {
      prevButton.parentElement.style.display = "flex";
      this.initCarouselControls(prevButton, nextButton);
    }

    if (sliderButtons) {
      sliderButtons.style.display = "flex";
      if (sliderCounter) {
        // Show total items in carousel
        sliderCounter.textContent = itemCount;
      }
    }
  }

  initCarouselControls(prevButton, nextButton) {
    console.log("[RecentlyViewedDB] Initializing carousel controls");

    // Remove existing event listeners to prevent duplicates
    const newPrevButton = prevButton.cloneNode(true);
    const newNextButton = nextButton.cloneNode(true);
    prevButton.parentNode.replaceChild(newPrevButton, prevButton);
    nextButton.parentNode.replaceChild(newNextButton, nextButton);

    const updateButtons = () => {
      const maxScroll = this.grid.scrollWidth - this.grid.clientWidth;
      newPrevButton.disabled = this.grid.scrollLeft <= 0;
      newNextButton.disabled = this.grid.scrollLeft >= maxScroll;
    };

    const getScrollAmount = () => {
      if (window.innerWidth < 750) return this.grid.clientWidth / 2; // Mobile
      if (window.innerWidth < 990) return this.grid.clientWidth / 3; // Tablet
      return this.grid.clientWidth / 4; // Desktop
    };

    newPrevButton.addEventListener("click", () => {
      this.grid.scrollBy({ left: -getScrollAmount(), behavior: "smooth" });
      setTimeout(updateButtons, 300);
    });

    newNextButton.addEventListener("click", () => {
      this.grid.scrollBy({ left: getScrollAmount(), behavior: "smooth" });
      setTimeout(updateButtons, 300);
    });

    // Update buttons on scroll
    this.grid.addEventListener("scroll", updateButtons);

    // Initial button state
    updateButtons();
  }

  showSkeletonLoaders() {
    // Make sure the skeleton wrapper is visible
    const wrapper = this.grid.querySelector(".rv-db__skeleton-wrapper");
    if (wrapper) {
      wrapper.style.display = "flex";
    }
  }

  clearSkeletonLoaders() {
    // Remove the skeleton wrapper
    const wrapper = this.grid.querySelector(".rv-db__skeleton-wrapper");
    if (wrapper) {
      wrapper.remove();
    }
  }
}

// --- INITIALIZATION ---
console.log("[RecentlyViewedDB] Script loaded, running main logic");

document.addEventListener("DOMContentLoaded", () => {
  console.log("[RecentlyViewedDB] DOMContentLoaded event fired");

  const recentlyViewedDB = new RecentlyViewedDB();

  if (recentlyViewedDB.customerId) {
    // Handle logged-in users
    const productJsonScript = document.querySelector('[id^="ProductJson-"]');
    console.log("[RecentlyViewedDB] productJsonScript:", productJsonScript);

    if (productJsonScript) {
      try {
        const productData = JSON.parse(productJsonScript.textContent);
        console.log("[RecentlyViewedDB] productData:", productData);

        if (productData && productData.handle) {
          recentlyViewedDB.track(productData.handle);
        }
      } catch (e) {
        console.error(
          "Error parsing product JSON for recently viewed tracking.",
          e
        );
      }
    } else {
      console.warn("[RecentlyViewedDB] No ProductJson script found on page.");
    }

    recentlyViewedDB.init();
  } else {
    // Handle guest users
    recentlyViewedDB.renderGuest();
  }

  // Inject recently viewed handles as a hidden input for client-side use
  function injectRecentlyViewedHandlesInput(handles) {
    let input = document.getElementById("recently-viewed-handles");
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.id = "recently-viewed-handles";
      input.name = "recently_viewed_handles";
      document.body.appendChild(input);
    }
    input.value = handles.join(",");
    console.log(
      "[RecentlyViewedDB] Injected hidden input with handles:",
      input.value
    );
  }

  // After tracking/init, inject the handles for client-side use
  let handles = [];
  try {
    handles = JSON.parse(
      localStorage.getItem("recently_viewed_handles") || "[]"
    );
  } catch (e) {
    handles = [];
  }

  if (Array.isArray(handles) && handles.length > 0) {
    injectRecentlyViewedHandlesInput(handles);
  }
});
